import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { CHAT } from '@/constants/testIds';
import { AudioPostPlayer } from './AudioPostPlayer';
import { timeLinesForPost } from '@/lib/formatTimes';

const MediaThumbnail = ({ item, onOpen }) => {
  if (item.kind === 'image') {
    return (
      <button
        type="button"
        className="bubble-media__item"
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        data-testid={CHAT.postMediaOpen}
        aria-label="Open image"
      >
        <img src={item.data_url} alt={item.name || 'image'} data-testid={CHAT.postImage} />
      </button>
    );
  }
  return (
    <button
      type="button"
      className="bubble-media__item"
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      data-testid={CHAT.postMediaOpen}
      aria-label="Open video"
    >
      <video src={item.data_url} muted playsInline preload="metadata" data-testid={CHAT.postVideo} />
    </button>
  );
};

export const ChatBubble = ({ post, isMine, isGlowing }) => {
  const [openMedia, setOpenMedia] = useState(null);
  const [flipped, setFlipped] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const text = post.text || '';
  const translation = post.translation || '';
  const hasMedia = Array.isArray(post.media) && post.media.length > 0;
  const hasAudio = !!post.audio;

  const onBubbleClick = (e) => {
    // Ignore clicks on interactive children (play button, media thumbnails,
    // close buttons, anchors). Those still do their own thing.
    if (e.target.closest('button, a, audio, [data-no-flip]')) return;
    setFlipped(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setFlipped(false), 5000);
  };

  const timeLines = flipped ? timeLinesForPost(post) : null;

  return (
    <li
      className={`chat-row ${isMine ? 'chat-row--mine' : 'chat-row--theirs'}`}
      data-testid={CHAT.postBubble}
    >
      <article
        className={`bubble ${isMine ? 'bubble--mine' : 'bubble--theirs'} ${isGlowing ? 'bubble--glow' : ''} ${flipped ? 'bubble--flipped' : ''}`.trim()}
        onClick={onBubbleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onBubbleClick(e);
          }
        }}
      >
        {flipped ? (
          <div className="bubble-times" data-testid={CHAT.bubbleTimes}>
            <span className="bubble-times__line" data-testid={CHAT.bubbleTimeLine}>
              {timeLines[0]}
            </span>
            <hr className="bubble-times__divider" data-testid={CHAT.postDivider} />
            <span className="bubble-times__line" data-testid={CHAT.bubbleTimeLine}>
              {timeLines[1]}
            </span>
          </div>
        ) : (
          <>
            {hasMedia && (
              <div className="bubble-media">
                {post.media.map((m, i) => (
                  <MediaThumbnail key={i} item={m} onOpen={() => setOpenMedia(m)} />
                ))}
              </div>
            )}

            {hasAudio && <AudioPostPlayer audio={post.audio} />}

            {text && (
              <div data-testid={CHAT.postText} className="bubble-original">
                {text}
              </div>
            )}

            {text && translation && (
              <hr className="bubble-divider" data-testid={CHAT.postDivider} />
            )}

            {translation && (
              <div data-testid={CHAT.postTranslation} className="bubble-translation">
                {translation}
              </div>
            )}
          </>
        )}
      </article>

      <Dialog open={!!openMedia} onOpenChange={(v) => !v && setOpenMedia(null)}>
        <DialogContent
          className="media-dialog__content p-0 border-0"
          data-testid={CHAT.postMediaDialog}
        >
          {openMedia?.kind === 'image' ? (
            <img src={openMedia.data_url} alt={openMedia.name || 'media'} />
          ) : openMedia?.kind === 'video' ? (
            <video src={openMedia.data_url} controls autoPlay>
              <track kind="captions" />
            </video>
          ) : null}
        </DialogContent>
      </Dialog>
    </li>
  );
};
